<template>
  <section class="grid grid-cols-12 gap-5">
    <div class="col-span-12 flex flex-col gap-5">
      <div class="flex items-center justify-between">
        <h4>
          {{ $t("stats.course.sidebar.engage") }}
        </h4>
      </div>
    </div>
    <template v-if="noDataAvailable">
      <div class="col-span-12 flex py-10 text-center">
        <Card :hasBorder="false" :isEmpty="true">
          <template #content>
            <h4 class="text-gray-500">
              {{
                $t("labels.generic", {
                  pt: "Dados não encontrados",
                  en: "Data unavailable",
                })
              }}
            </h4>
          </template>
        </Card>
      </div>
    </template>
    <template v-else>
      <div class="col-span-12 md:col-span-6">
        <Card :hasBorder="false" :isEmpty="true">
          <template #content>
            <div class="flex gap-2">
              <h4 class="mb-2">
                {{
                  $t("labels.generic", {
                    pt: "Ranking de atributos para os não engajados",
                    en: "Students who turned detection off",
                  })
                }}
              </h4>
              <div
                class="w-fit flex items-start justify-center pt-3 px-1 sm:p-1 sm:px-0 sm:pt-1 sm:items-center"
              >
                <i
                  class="btn-simple !p0 pi pi-info-circle !text-[1.3rem]"
                  @click="visible = true"
                />
              </div>
              <div>
                <Dialog
                  v-model:visible="visible"
                  modal
                  :header="
                    $t('labels.generic', {
                      pt: 'Atributos',
                      en: 'Attributes',
                    })
                  "
                  :style="{ width: '50vw' }"
                  :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
                >
                  <Info />
                </Dialog>
              </div>
            </div>
            <div class="grid grid-cols-12 items-center">
              <div class="col-span-8 mt-10 gap-7">
                <div class="flex justify-center text-end">
                  <RankItem
                    v-for="(item, index) in rankItems"
                    :key="index"
                    :src="item.src"
                    :height="item.height"
                  />
                </div>
              </div>
              <div class="col-span-4 flex flex-col gap-5">
                <div
                  v-for="(rank, index) in Object.values(ranks)"
                  :key="index"
                  class="items-center flex flex-col text-center"
                >
                  <img :src="getImageSrc(index)" alt="" class="h-14" />
                  <p>{{ rank }}</p>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
      <div
        v-for="(chart, index) in engagementCharts"
        :key="index"
        class="col-span-12 md:col-span-6"
      >
        <Card :hasBorder="false" :isEmpty="true">
          <template #content>
            <h4 class="mb-2" v-html="chart.title" />
            <div class="grid grid-cols-12 items-center">
              <div class="col-span-8">
                <ApexChart
                  type="donut"
                  :options="chart.data.options"
                  :series="chart.data.series"
                ></ApexChart>
              </div>
              <div class="col-span-4 flex flex-col gap-5">
                <div v-for="(item, i) in chart.data.legends" :key="i">
                  <div class="flex items-center gap-1">
                    <h5>{{ item.title }}</h5>
                  </div>
                  <div class="flex items-center gap-3">
                    <p :class="`${item.color} h-2 w-2 rounded-full`"></p>
                    <h3>{{ item.number }}</h3>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import Info from "./info.vue";
import { defineComponent } from "vue";
import { LeanRest } from "@/services/rest/lean.rest";
import { defineAsyncComponent } from "vue";
import { exportOptions } from "../General/charts";
import { QueryParams } from "@/models/query-params.model";
import RankItem from "./rank-item.vue";
import { useThemeStore } from "@/store/theme";
const Card = defineAsyncComponent(() => import("../../Card/card.vue"));

export default defineComponent({
  name: "generalInfo",
  components: { Card, Info, RankItem },
  data() {
    return {
      rankItems: [
        { src: require("@/assets/images/engage/third.svg"), height: "h-20" },
        {
          src: require("@/assets/images/engage/first.svg"),
          height: "h-48 lg:mx-8",
        },
        { src: require("@/assets/images/engage/second.svg"), height: "h-32" },
      ],
      params: new QueryParams(),
      noDataAvailable: false,
      visible: false,
      ranks: {
        rank1: "",
        rank2: "",
        rank3: "",
      },
      engagementCharts: [
        {
          title: this.$t("labels.generic", {
            pt: "Alunos engajados",
            en: "Engaged vs Disengaged Students",
          }),
          data: {
            series: [] as number[],
            options: exportOptions.att,
            legends: [] as { title: string; number: number; color: string }[],
          },
        },
        {
          title: this.$t("labels.generic", {
            pt: "Alunos que <strong>concluíram</strong> o curso",
            en: "Students who <strong>completed</strong> the course",
          }),
          data: {
            series: [] as number[],
            options: exportOptions.att,
            legends: [] as { title: string; number: number; color: string }[],
          },
        },
        {
          title: this.$t("labels.generic", {
            pt: "Alunos que <strong>não concluíram</strong> o curso",
            en: "Students who did <strong>not completed</strong> the course",
          }),
          data: {
            series: [] as number[],
            options: exportOptions.att,
            legends: [] as { title: string; number: number; color: string }[],
          },
        },
      ],
    };
  },
  async created() {
    await useThemeStore.getTheme();
  },
  mounted() {
    this.getLeanStats();
  },
  methods: {
    async getLeanStats() {
      const courseId = this.$route.params.courseId as string;
      this.params.course = courseId;

      if (!useThemeStore.theme) await useThemeStore.getTheme();

      this.leanRest.leanStats(this.params).subscribe({
        next: (response) => {
          this.setRanks(response.rank);
          this.setCharts(response);
          this.noDataAvailable = this.isDataEmpty();
        },
        error: () => {
          this.noDataAvailable = true;
        },
      });
    },
    setRanks(rankData: { rank1?: string; rank2?: string; rank3?: string }) {
      this.ranks.rank1 = this.$t(
        `lean.statsAttributes.${rankData.rank1 || "N/A"}`
      );
      this.ranks.rank2 = this.$t(
        `lean.statsAttributes.${rankData.rank2 || "N/A"}`
      );
      this.ranks.rank3 = this.$t(
        `lean.statsAttributes.${rankData.rank3 || "N/A"}`
      );
    },
    setCharts(response: any) {
      const chartConfigs = [
        {
          index: 0,
          engagedKey: "Engajado",
          disengagedKey: "Não engajado",
        },
        {
          index: 1,
          engagedKey: "Engajado curso concluido",
          disengagedKey: "Não engajado curso concluido",
        },
        {
          index: 2,
          engagedKey: "Engajado curso não concluido",
          disengagedKey: "Não engajado curso não concluido",
        },
      ];
      chartConfigs.forEach(({ index, engagedKey, disengagedKey }) => {
        this.updateChart(
          index,
          response[engagedKey] ?? 0,
          response[disengagedKey] ?? 0
        );
      });
    },
    updateChart(index: number, engaged: number, disengaged: number) {
      const chart = this.engagementCharts[index];
      chart.data.series = [engaged, disengaged];

      if (useThemeStore.theme?.colors) {
        chart.data.options = {
          ...exportOptions.att,
          fill: {
            colors: [useThemeStore.theme.colors["h-primary"][500], "#97979B"],
          },
        };
      }

      chart.data.legends = [
        {
          title: this.$t("stats.course.charts.engage.engaged"),
          number: engaged,
          color: "bg-h-primary-800",
        },
        {
          title: this.$t("stats.course.charts.engage.disengaged"),
          number: disengaged,
          color: "bg-gray-500",
        },
      ];
    },
    getImageSrc(index: number) {
      const images = ["first", "second", "third"];
      return require(`@/assets/images/engage/${images[index]}.svg`);
    },
    configureChart(index, engagedKey, disengagedKey, response) {
      const engaged = response[engagedKey] || 0;
      const disengaged = response[disengagedKey] || 0;
      this.engagementCharts[index].data.series = [engaged, disengaged];
      this.engagementCharts[index].data.legends = [
        {
          title: this.$t("stats.course.charts.engage.engaged"),
          number: engaged,
          color: "bg-h-primary-800",
        },
        {
          title: this.$t("stats.course.charts.engage.disengaged"),
          number: disengaged,
          color: "bg-gray-500",
        },
      ];
    },
    isDataEmpty() {
      return this.engagementCharts.every(
        (chart) =>
          chart.data.series.length === 0 ||
          chart.data.series.every((value) => value === 0)
      );
    },
  },
  computed: {
    leanRest(): LeanRest {
      return new LeanRest();
    },
  },
});
</script>
